import {
  ContextMenuItem,
  ContextMenuSurface,
} from '@src/components/common/contextMenuStyles';
import CreateIcon from '@src/svgs/CreateIcon';
import DeleteIcon from '@src/svgs/DeleteIcon';
import QuoteStartIcon from '@src/svgs/QuoteStartIcon';
import SimplePencilIcon from '@src/svgs/SimplePencilIcon';
import VideoIcon from '@src/svgs/VideoIcon';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';

export type HighlightActionPopoverProps = {
  /** Bounding rect of the hovered span — anchor for positioning. */
  anchorRect: DOMRect;
  /** Quote id if the hovered span is part of a quote highlight range. */
  quoteId: string | null;
  /** Clip id if the hovered span is part of a clip highlight range. */
  clipId: string | null;
  /**
   * PLAT-10045 — Overlay composition id if the hovered span is within an
   * overlay/other/quoteCard composition placement's time range. Optional
   * so non-clip-preview callers don't have to thread `null` explicitly.
   */
  overlayId?: string | null;
  /** Invoked when the user clicks the quote action. */
  onOpenQuote: (id: string) => void;
  /** Invoked when the user clicks the clip action. */
  onOpenClip: (id: string) => void;
  /** Invoked when the user clicks the overlay action (opens CompositionQuickEditPopover with targetType: 'overlay'). */
  onOpenOverlay?: (id: string) => void;
  /**
   * Saved-segment group (epoch-ms key) id if the hovered span is within a
   * saved segment's range — the save-for-later → combine flow. Optional so
   * other callers don't have to thread `null`.
   */
  savedSegmentId?: string | null;
  /** Name of the hovered saved segment group, surfaced in the Remove tooltip. */
  segmentName?: string;
  /**
   * Number of ranges in the hovered group. 1 → offer "Create Clip"; 2+ →
   * offer "Combine into Reel".
   */
  segmentRangeCount?: number;
  /** Create a single clip from a one-range segment group. */
  onCreateClipFromSegment?: (id: string) => void;
  /** Combine the group's ranges into one compilation reel. */
  onCombineSegment?: (id: string) => void;
  /** Rename the saved segment group (secondary naming action). */
  onRenameSegment?: (id: string, name: string) => void;
  /** Permanently delete the saved segment group. */
  onRemoveSegment?: (id: string) => void;
  /** Mouse moved into the popover — caller should cancel the close timer. */
  onMouseEnter: () => void;
  /** Mouse moved out of the popover — caller should re-arm the close timer. */
  onMouseLeave: () => void;
};

const POPOVER_GAP_PX = 6;
const VIEWPORT_PADDING_PX = 8;

// Grow the rename textarea to fit its content so a long prefilled name wraps
// fully into view (capped by the CSS max-height, which then scrolls).
const autoSizeTextarea = (el: HTMLTextAreaElement) => {
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight}px`;
};

export const HighlightActionPopover: React.FC<HighlightActionPopoverProps> = ({
  anchorRect,
  quoteId,
  clipId,
  overlayId,
  savedSegmentId,
  segmentName,
  segmentRangeCount,
  onCreateClipFromSegment,
  onCombineSegment,
  onRenameSegment,
  onRemoveSegment,
  onOpenQuote,
  onOpenClip,
  onOpenOverlay,
  onMouseEnter,
  onMouseLeave,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  // Secondary naming: the popover swaps to an inline input when renaming.
  const [isRenaming, setIsRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState('');

  // On open, size the textarea to its content and select the whole name so a
  // keystroke replaces it. Anchor the selection *backward* (focus at index 0)
  // so a long prefilled name scrolls to its start, not its tail.
  useLayoutEffect(() => {
    if (!isRenaming) return;
    const input = inputRef.current;
    if (!input) return;
    autoSizeTextarea(input);
    input.focus();
    input.setSelectionRange(0, input.value.length, 'backward');
    input.scrollTop = 0;
  }, [isRenaming]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();

    const flipBelow =
      anchorRect.top - height - POPOVER_GAP_PX < VIEWPORT_PADDING_PX;
    const top = flipBelow
      ? anchorRect.bottom + POPOVER_GAP_PX
      : anchorRect.top - height - POPOVER_GAP_PX;

    const rawLeft = anchorRect.left + anchorRect.width / 2 - width / 2;
    const maxLeft = window.innerWidth - width - VIEWPORT_PADDING_PX;
    const left = Math.max(VIEWPORT_PADDING_PX, Math.min(rawLeft, maxLeft));

    setPos({ top, left });
    // Re-measure when the content height changes (menu ⇄ rename input).
  }, [anchorRect, isRenaming]);

  // Close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onMouseLeave();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onMouseLeave]);

  const hasSavedSegmentActions = !!savedSegmentId && !!onRemoveSegment;
  const canCombine = (segmentRangeCount ?? 0) >= 2;

  if (!quoteId && !clipId && !overlayId && !hasSavedSegmentActions) return null;

  const commitRename = () => {
    const name = nameDraft.trim();
    setIsRenaming(false);
    if (name && savedSegmentId) onRenameSegment?.(savedSegmentId, name);
  };

  const content = (
    <Wrapper
      ref={ref}
      style={pos ? { top: pos.top, left: pos.left } : { visibility: 'hidden' }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onMouseDown={e => e.stopPropagation()}
      onMouseUp={e => e.stopPropagation()}
      onClick={e => e.stopPropagation()}
      data-testid="transcript-highlight-action-popover"
    >
      {isRenaming && savedSegmentId && onRenameSegment ? (
        <RenameForm
          onSubmit={e => {
            e.preventDefault();
            commitRename();
          }}
          data-testid="transcript-highlight-action-rename-form"
        >
          <RenameInput
            ref={inputRef}
            rows={2}
            value={nameDraft}
            placeholder="Highlight name"
            onChange={e => {
              setNameDraft(e.target.value);
              autoSizeTextarea(e.currentTarget);
            }}
            onKeyDown={e => {
              if (e.key === 'Escape') {
                e.preventDefault();
                setIsRenaming(false);
              } else if (e.key === 'Enter') {
                // Names are single-line: Enter saves instead of adding a break.
                e.preventDefault();
                commitRename();
              }
            }}
          />
          <RenameActions>
            <RenameButton type="button" onClick={() => setIsRenaming(false)}>
              Cancel
            </RenameButton>
            <RenameButton type="submit" $primary disabled={!nameDraft.trim()}>
              Save
            </RenameButton>
          </RenameActions>
        </RenameForm>
      ) : (
        <>
          {quoteId && (
            <MenuItem
              type="button"
              onClick={e => {
                e.stopPropagation();
                onOpenQuote(quoteId);
              }}
              data-testid="transcript-highlight-action-open-quote"
              title="Open in Pull Quotes editor"
            >
              <span>Edit Quote</span>
              <IconBox>
                <QuoteStartIcon
                  width="14"
                  height="12"
                  fillColor="currentColor"
                />
              </IconBox>
            </MenuItem>
          )}
          {clipId && (
            <MenuItem
              type="button"
              onClick={e => {
                e.stopPropagation();
                onOpenClip(clipId);
              }}
              data-testid="transcript-highlight-action-open-clip"
              title="Open clip preview"
            >
              <span>Edit Clip</span>
              <IconBox>
                <VideoIcon
                  width="16"
                  height="14"
                  viewBox="0 0 14 14"
                  strokeColor="currentColor"
                />
              </IconBox>
            </MenuItem>
          )}
          {overlayId && onOpenOverlay && (
            <MenuItem
              type="button"
              onClick={e => {
                e.stopPropagation();
                onOpenOverlay(overlayId);
              }}
              data-testid="transcript-highlight-action-open-overlay"
              title="Edit overlay text"
            >
              <span>Edit Overlay</span>
              <IconBox>
                <SimplePencilIcon width="14" height="14" />
              </IconBox>
            </MenuItem>
          )}
          {hasSavedSegmentActions && (
            <>
              {canCombine
                ? onCombineSegment && (
                    <MenuItem
                      type="button"
                      onClick={e => {
                        e.stopPropagation();
                        onCombineSegment(savedSegmentId!);
                        // Committed — nothing left to do here, and the work
                        // is reported by a toast. Leaving the menu up over
                        // the transcript reads as if the click didn't take.
                        onMouseLeave();
                      }}
                      data-testid="transcript-highlight-action-combine-segment"
                      title="Combine this highlight into one reel"
                    >
                      <span>Combine into Clip</span>
                      <IconBox>
                        <VideoIcon
                          width="16"
                          height="14"
                          viewBox="0 0 14 14"
                          strokeColor="currentColor"
                        />
                      </IconBox>
                    </MenuItem>
                  )
                : onCreateClipFromSegment && (
                    <MenuItem
                      type="button"
                      onClick={e => {
                        e.stopPropagation();
                        onCreateClipFromSegment(savedSegmentId!);
                        onMouseLeave();
                      }}
                      data-testid="transcript-highlight-action-create-clip"
                      title="Create a clip from this highlight"
                    >
                      <span>Create Clip from Highlight</span>
                      <IconBox>
                        <CreateIcon
                          width="16"
                          height="14"
                          strokeColor="currentColor"
                        />
                      </IconBox>
                    </MenuItem>
                  )}
              {onRenameSegment && (
                <MenuItem
                  type="button"
                  onClick={e => {
                    e.stopPropagation();
                    setNameDraft(segmentName ?? '');
                    setIsRenaming(true);
                  }}
                  data-testid="transcript-highlight-action-rename-segment"
                  title="Rename this highlight"
                >
                  <span>Rename Highlight</span>
                  <IconBox>
                    <SimplePencilIcon width="14" height="14" />
                  </IconBox>
                </MenuItem>
              )}
              <MenuItem
                type="button"
                $danger
                onClick={e => {
                  e.stopPropagation();
                  onRemoveSegment!(savedSegmentId!);
                }}
                data-testid="transcript-highlight-action-remove-segment"
                title={
                  segmentName
                    ? `Delete highlight “${segmentName}”`
                    : 'Delete this highlight'
                }
              >
                <span>Remove Highlight</span>
                <IconBox>
                  <DeleteIcon
                    width="14"
                    height="14"
                    strokeColor="currentColor"
                  />
                </IconBox>
              </MenuItem>
            </>
          )}
        </>
      )}
    </Wrapper>
  );

  return ReactDOM.createPortal(content, document.body);
};

const Wrapper = styled(ContextMenuSurface)`
  /* Shared floor so the rename form (which replaces the menu items while
     renaming) stays as wide as the resting menu instead of collapsing. */
  min-width: 240px;
  font-size: var(--text-xs);
`;

/* Labels are single-line — the popover widens rather than wrapping a row. */
const MenuItem = styled(ContextMenuItem)`
  white-space: nowrap;
`;

const IconBox = styled.span`
  margin-left: auto;
  display: flex;
  align-items: center;
`;

const RenameForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px;
  box-sizing: border-box;
  width: 360px;
  max-width: calc(100vw - 16px);
`;

const RenameInput = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  padding: 6px 8px;
  font: inherit;
  font-size: var(--text-xs);
  line-height: 1.4;
  color: var(--foreground);
  background: var(--input-background, var(--background));
  border: 1px solid var(--input-border, var(--border));
  border-radius: var(--radius);
  resize: none;
  overflow-y: auto;
  max-height: 160px;
  white-space: pre-wrap;
  word-break: break-word;

  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const RenameActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 6px;
`;

const RenameButton = styled.button<{ $primary?: boolean }>`
  cursor: pointer;
  padding: 5px 12px;
  font: inherit;
  font-size: var(--text-xs);
  border-radius: var(--radius);
  border: 1px solid
    ${props => (props.$primary ? 'var(--primary)' : 'var(--border)')};
  background: ${props => (props.$primary ? 'var(--primary)' : 'transparent')};
  color: ${props =>
    props.$primary
      ? 'var(--primary-foreground, var(--background))'
      : 'inherit'};

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

export default HighlightActionPopover;
